import React, { useState } from 'react';
import { uploadImageToS3 } from '../services/s3Service';
import { 
    Box, 
    Typography, 
    Button, 
    CircularProgress, 
    alpha
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const ImageUpload = ({
    onUpload,
    onUploadingChange,
    currentImage,
    folder = 'uploads/',
    label = 'Image',
    accept = 'image/*'
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [fileName, setFileName] = useState('');

    const inputId = `file-upload-${folder}-${Math.random().toString(36).substr(2, 9)}`;

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);
        setUploading(true);
        if (typeof onUploadingChange === 'function') onUploadingChange(true);
        setError(null);
        setSuccess(false);

        try {
            const result = await uploadImageToS3(file, folder);
            onUpload(result);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError("Falha no upload. Tente novamente.");
            console.error(err);
        } finally {
            setUploading(false);
            if (typeof onUploadingChange === 'function') onUploadingChange(false);
        }
    };

    const isVideo = currentImage && (currentImage.toLowerCase().endsWith('.mp4') || currentImage.toLowerCase().endsWith('.webm') || currentImage.toLowerCase().endsWith('.mov'));

    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#444' }}>
                {label}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ position: 'relative' }}>
                        <input
                            id={inputId}
                            type="file"
                            accept={accept}
                            onChange={handleFileChange}
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                        <Button
                            component="label"
                            htmlFor={inputId}
                            variant="outlined"
                            disabled={uploading}
                            startIcon={
                                uploading ? <CircularProgress size={20} color="inherit" /> : 
                                success ? <CheckCircleIcon /> : <CloudUploadIcon />
                            }
                            sx={{
                                textTransform: 'none',
                                px: 3,
                                py: 1.5,
                                backgroundColor: uploading ? '#f5f5f5' : 'white',
                                borderColor: '#ddd',
                                color: '#333',
                                borderRadius: '12px',
                                borderStyle: 'dashed',
                                borderWidth: 2,
                                transition: 'all 0.3s',
                                '&:hover': {
                                    borderColor: '#cc0000',
                                    backgroundColor: alpha('#cc0000', 0.02),
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {uploading ? 'Processando...' : success ? 'Concluído!' : 'Fazer Upload'}
                        </Button>
                        
                        <Box sx={{ mt: 1, fontSize: '0.9rem', color: '#666', display: 'flex', alignItems: 'center', gap: 1 }}>
                           {fileName && <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{fileName}</Typography>}
                           {success && <CheckCircleIcon sx={{ color: 'green', fontSize: '1.2rem' }} />}
                           {error && (
                               <Box sx={{ color: 'red', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                   <ErrorIcon sx={{ fontSize: '1.2rem' }} /> {error}
                               </Box>
                           )}
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: '#999', mt: 0.75 }}>
                        {accept === 'video/*' ? 'Formatos: MP4, WEBM. Máx: 50MB.' : 'Formatos: JPG, PNG, WEBP. Máx: 5MB.'}
                    </Typography>
                </Box>

                {currentImage && (
                    <Box 
                        sx={{ 
                            width: 120, // Increased to 120 to match MultiImageUpload
                            height: 120, 
                            border: '2px solid #eee', 
                            borderRadius: '16px', 
                            overflow: 'hidden', 
                            flexShrink: 0, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            backgroundColor: '#fff', 
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                            }
                        }}
                    >
                        {isVideo ? (
                            <Typography sx={{ color: '#555', fontSize: '10px', textAlign: 'center', fontWeight: 'bold' }}>
                                VIDEO<br />FILE
                            </Typography>
                        ) : (
                            <Box 
                                component="img"
                                src={currentImage} 
                                alt="Preview" 
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ImageUpload;
